import { fieldTicketCreateActionSchema, fieldTicketRefineActionSchema } from '@mio/shared';
import { useId, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { FieldChoiceList } from './field-discovery-inputs';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import type { FieldPanelProps } from './field-types';
import type { FieldFinding, FieldOutcomeId, FieldTicket } from '@mio/shared';

export function FieldTicketForm({
  snapshot,
  busy,
  onAction,
  ticket,
  finding,
  onClose,
}: FieldPanelProps & {
  ticket?: FieldTicket;
  finding?: FieldFinding;
  onClose: () => void;
}) {
  const id = useId();
  const [expectedVersion] = useState(ticket?.version ?? 1);
  const [title, setTitle] = useState(ticket?.title ?? finding?.title ?? '');
  const [description, setDescription] = useState(ticket?.description ?? finding?.description ?? '');
  const [owner, setOwner] = useState(ticket?.owner ?? finding?.owner ?? 'jordan');
  const [priority, setPriority] = useState<'high' | 'normal' | 'low'>(ticket?.priority ?? 'normal');
  const [acceptance, setAcceptance] = useState(ticket?.acceptance.join('\n') ?? '');
  const [dependencies, setDependencies] = useState(ticket?.dependencies ?? []);
  const [decisionIds, setDecisionIds] = useState(ticket?.decisionIds ?? finding?.decisionIds ?? []);
  const [outcomeIds, setOutcomeIds] = useState<FieldOutcomeId[]>(ticket?.outcomeIds ?? []);
  const [error, setError] = useState('');
  return (
    <form
      className="field-object-form space-y-4"
      aria-label={ticket ? 'Refine ticket' : 'Create ticket'}
      onSubmit={(event) => {
        event.preventDefault();
        const fields = {
          title,
          description,
          owner,
          priority,
          decisionIds,
          outcomeIds,
          dependencies,
          acceptance: acceptance
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean),
        };
        const parsed = ticket
          ? fieldTicketRefineActionSchema.safeParse({
              type: 'ticket.refine',
              ticketId: ticket.id,
              expectedVersion,
              ...fields,
            })
          : fieldTicketCreateActionSchema.safeParse({
              type: 'ticket.create',
              ...fields,
              ...(finding ? { findingId: finding.id } : {}),
            });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? 'Check this ticket and try again.');
          return;
        }
        setError('');
        void onAction(parsed.data).then((saved) => {
          if (saved) onClose();
        });
      }}
    >
      <div>
        <h3 className="text-base font-semibold">
          {ticket ? 'Refine ticket' : finding ? 'Plan the follow-up' : 'Create a ticket'}
        </h3>
        <p className="text-fg-muted mt-2 text-sm leading-6">
          {finding
            ? 'Link the follow-up to this finding and define what will demonstrate resolution.'
            : 'Choose a useful increment. Describe the result, how you will verify it, and who should review it.'}
        </p>
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-title`}>
          Ticket title
        </label>
        <input
          id={`${id}-title`}
          className={FIELD_INPUT}
          required
          maxLength={200}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-description`}>
          What will change and why
        </label>
        <textarea
          id={`${id}-description`}
          className={FIELD_INPUT}
          required
          rows={4}
          maxLength={12000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div className="field-object-form-grid">
        <div>
          <label className={FIELD_LABEL} htmlFor={`${id}-owner`}>
            Review owner
          </label>
          <SelectField
            id={`${id}-owner`}
            value={owner}
            onValueChange={(value) => {
              const actor = snapshot.actors.find((item) => item.id === value);
              if (actor) setOwner(actor.id);
            }}
            options={snapshot.actors.map((actor) => ({
              value: actor.id,
              label: `${actor.name} · ${actor.role}`,
            }))}
          />
        </div>
        <div>
          <label className={FIELD_LABEL} htmlFor={`${id}-priority`}>
            Priority
          </label>
          <SelectField
            id={`${id}-priority`}
            value={priority}
            onValueChange={(value) => {
              if (value === 'high' || value === 'normal' || value === 'low') setPriority(value);
            }}
            options={[
              { value: 'high', label: 'High' },
              { value: 'normal', label: 'Normal' },
              { value: 'low', label: 'Low' },
            ]}
          />
        </div>
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor={`${id}-acceptance`}>
          Acceptance criteria (one per line)
        </label>
        <textarea
          id={`${id}-acceptance`}
          className={FIELD_INPUT}
          required
          rows={3}
          maxLength={40000}
          value={acceptance}
          onChange={(event) => setAcceptance(event.target.value)}
        />
        <p className="text-fg-muted mt-1 text-xs leading-5">
          Use observable behavior or an example that can be verified.
        </p>
      </div>
      <FieldChoiceList
        label="Outcomes this supports"
        value={outcomeIds}
        max={3}
        options={
          snapshot.discovery?.outcomes.map((outcome) => ({
            id: outcome.id,
            label: outcome.title,
          })) ?? []
        }
        onChange={(values) =>
          setOutcomeIds(
            snapshot.discovery?.outcomes
              .filter((item) => values.includes(item.id))
              .map((item) => item.id) ?? [],
          )
        }
      />
      <FieldChoiceList
        label="Related decisions"
        value={decisionIds}
        onChange={setDecisionIds}
        options={
          snapshot.discovery?.decisions.map((decision) => ({
            id: decision.id,
            label: `${decision.title} · ${decision.status}`,
          })) ?? []
        }
        empty="You can link decisions as you clarify the scope."
      />
      {snapshot.tickets.some((item) => item.id !== ticket?.id) ? (
        <FieldChoiceList
          label="Depends on"
          value={dependencies}
          onChange={setDependencies}
          options={snapshot.tickets
            .filter((item) => item.id !== ticket?.id)
            .map((item) => ({ id: item.id, label: item.title }))}
        />
      ) : null}
      {error ? (
        <p role="alert" className="text-danger text-sm">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          disabled={
            busy || !snapshot.enabled || !title.trim() || !description.trim() || !acceptance.trim()
          }
        >
          {ticket ? 'Save changes' : 'Create ticket'}
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
